<!-- Tab bar -->
<div class="flex border-b border-slate-200 px-6" role="tablist">
  <button
    *ngFor="let tab of tabs"
    (click)="selectedTab = tab.id"
    role="tab"
    [attr.aria-selected]="selectedTab === tab.id"
    class="mr-6 border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none"
    [class.border-violet-600]="selectedTab === tab.id"
    [class.text-violet-700]="selectedTab === tab.id"
    [class.border-transparent]="selectedTab !== tab.id"
    [class.text-slate-500]="selectedTab !== tab.id"
    [class.hover:text-slate-700]="selectedTab !== tab.id"
  >
    {{ tab.label }}
  </button>
</div>

<!-- ── Overview ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">

  <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Pipelines</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
    </div>
    <div class="rounded-lg border p-4"
         [class.border-red-200]="failedCount > 0"
         [class.bg-red-50]="failedCount > 0"
         [class.border-slate-200]="failedCount === 0"
         [class.bg-slate-50]="failedCount === 0">
      <p class="text-xs font-medium uppercase tracking-wide"
         [class.text-red-500]="failedCount > 0"
         [class.text-slate-400]="failedCount === 0">Failed</p>
      <p class="mt-1 text-2xl font-bold"
         [class.text-red-700]="failedCount > 0"
         [class.text-slate-900]="failedCount === 0">{{ failedCount }}</p>
    </div>
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">In Progress</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ inProgressCount }}</p>
    </div>
  </div>

</div>

<!-- ── Pipelines ──────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'executions'" role="tabpanel">

  <!-- Filter bar spans full width -->
  <app-cloud-filter-bar
    entityName="pipelines"
    placeholder="Search pipeline name…"
    [facets]="facets"
    [sortOptions]="sortOptions"
    [filters]="filters"
    [resultCount]="filteredPipelines.length"
    [totalCount]="pipelines.length"
    [quotaUsage]="quotaUsage"
    (filtersChange)="onFiltersChange($event)"
  ></app-cloud-filter-bar>

  <!-- Content row: table + optional run-history panel -->
  <div class="flex min-h-0" [class.divide-x]="selectedPipeline" [class.divide-slate-200]="selectedPipeline">

    <!-- Table area -->
    <div class="min-w-0 flex-1 p-6">

      <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading pipelines…
      </div>

      <div *ngIf="!loading" class="overflow-x-auto">
        <table class="w-full min-w-[660px] text-left text-sm">
          <thead>
            <tr class="border-b border-slate-200">
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Pipeline</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Last Execution</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Failed Stage</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Routing</th>
              <th class="pb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let p of filteredPipelines"
                class="border-b border-slate-100 last:border-0"
                [class.bg-violet-50]="p.name === selectedPipeline">
              <td class="py-3 pr-4 font-mono text-xs font-medium text-slate-900">{{ p.name }}</td>
              <td class="py-3 pr-4 text-xs text-slate-600">{{ p.lastExecutionAt | cloudDate }}</td>
              <td class="py-3 pr-4 text-xs text-slate-500">{{ p.failedStage ?? '—' }}</td>
              <td class="py-3 pr-4">
                <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      [class]="statusClass(p.status)">
                  {{ p.status }}
                </span>
              </td>
              <!-- Monitoring -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <ng-container *ngIf="isMonitored(p.name); else pipeNotMonitored">
                  <button
                    type="button"
                    (click)="toggleMonitoring(p.name)"
                    class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    aria-label="Disable monitoring"
                  >
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                    Monitored
                  </button>
                </ng-container>
                <ng-template #pipeNotMonitored>
                  <ng-container *ngIf="canMonitorMore; else pipeQuotaFull">
                    <button
                      type="button"
                      (click)="toggleMonitoring(p.name)"
                      class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    >
                      Monitor
                    </button>
                  </ng-container>
                  <ng-template #pipeQuotaFull>
                    <span class="group relative inline-block">
                      <button
                        type="button"
                        (click)="toggleMonitoring(p.name)"
                        aria-disabled="true"
                        class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none"
                      >
                        Monitor
                      </button>
                      <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        Quota reached
                      </span>
                    </span>
                  </ng-template>
                </ng-template>
              </td>
              <!-- Routing: override indicator + gear -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <div class="flex items-center gap-1.5">
                  <span *ngIf="itemOverrideSummary(p.name)"
                        class="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                    {{ itemOverrideSummary(p.name) }}
                  </span>
                  <button
                    type="button"
                    (click)="openItemSettings(p.name)"
                    class="flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    [class.text-violet-500]="!!itemOverrideSummary(p.name)"
                    [attr.aria-label]="'Configure routing for ' + p.name"
                  >
                    <lucide-icon name="settings" [size]="12" [strokeWidth]="2"></lucide-icon>
                  </button>
                </div>
              </td>
              <td class="py-3 text-right">
                <button
                  (click)="p.name === selectedPipeline ? closePipeline() : selectPipeline(p.name)"
                  class="rounded px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.text-violet-600]="p.name === selectedPipeline"
                  [class.hover:text-violet-700]="p.name === selectedPipeline"
                >
                  {{ p.name === selectedPipeline ? 'Close' : 'View runs' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty state: no matches -->
        <div *ngIf="filteredPipelines.length === 0 && pipelines.length > 0" class="py-10 text-center">
          <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
          <p class="text-sm font-medium text-slate-500">No pipelines match your filters</p>
          <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
        </div>

        <!-- Empty state: no data -->
        <p *ngIf="pipelines.length === 0" class="py-6 text-sm text-slate-400">
          No CodePipeline pipelines found in this account and region.
        </p>
      </div>

    </div>

    <!-- Run history panel -->
    <div *ngIf="selectedPipeline" class="flex w-96 shrink-0 flex-col">

      <!-- Panel header -->
      <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div class="flex min-w-0 items-center gap-2">
          <lucide-icon name="clock" [size]="14" class="shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
          <span class="truncate font-mono text-xs font-semibold text-slate-800">{{ selectedPipeline }}</span>
        </div>
        <button
          (click)="closePipeline()"
          class="ml-2 shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
          aria-label="Close run history"
        >
          <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
        </button>
      </div>

      <p class="border-b border-slate-100 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        Run History
      </p>

      <!-- Panel body -->
      <div class="flex-1 overflow-y-auto">

        <!-- Loading -->
        <div *ngIf="historyLoading" class="flex items-center gap-2 px-4 py-6 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading…
        </div>

        <!-- Error -->
        <p *ngIf="historyError && !historyLoading" class="px-4 py-4 text-xs text-red-500">
          {{ historyError }}
        </p>

        <!-- Execution list -->
        <div *ngIf="!historyLoading && !historyError">

          <div *ngIf="executionHistory.length === 0" class="px-4 py-6 text-sm text-slate-400">
            No executions found.
          </div>

          <div *ngFor="let ex of pagedExecutionHistory"
               class="border-b border-slate-100 px-4 py-3 last:border-0">
            <div class="flex items-start justify-between gap-2">
              <span class="inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    [class]="historyStatusClass(ex.status)">
                {{ ex.status }}
              </span>
              <span class="truncate font-mono text-[10px] text-slate-400">{{ ex.executionId }}</span>
            </div>
            <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
              <span *ngIf="ex.startTime">{{ ex.startTime | cloudDate }}</span>
              <span *ngIf="ex.trigger" class="text-slate-400">{{ ex.trigger }}</span>
            </div>
          </div>

          <!-- Pagination -->
          <div *ngIf="historyTotalPages > 1"
               class="flex items-center justify-between border-t border-slate-100 px-4 py-2">
            <p class="text-[11px] text-slate-400">
              Page {{ historyPage }} of {{ historyTotalPages }}
            </p>
            <div class="flex items-center gap-1">
              <button
                (click)="historyPage = historyPage - 1"
                [disabled]="historyPage === 1"
                class="rounded px-2 py-1 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.text-slate-300]="historyPage === 1"
                [class.cursor-not-allowed]="historyPage === 1"
                [class.text-slate-600]="historyPage !== 1"
                [class.hover:bg-slate-100]="historyPage !== 1"
              >Prev</button>
              <button
                (click)="historyPage = historyPage + 1"
                [disabled]="historyPage === historyTotalPages"
                class="rounded px-2 py-1 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.text-slate-300]="historyPage === historyTotalPages"
                [class.cursor-not-allowed]="historyPage === historyTotalPages"
                [class.text-slate-600]="historyPage !== historyTotalPages"
                [class.hover:bg-slate-100]="historyPage !== historyTotalPages"
              >Next</button>
            </div>
          </div>

        </div>

      </div>
    </div>

  </div>

</div>

<!-- ── Activity ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'activity'" role="tabpanel">
  <app-cloud-activity-feed
    [items]="activity"
    [loading]="activityLoading"
    serviceLabel="CodePipeline"
    emptyHint="Recent pipeline executions and failure findings appear here."
    (itemSelect)="onActivitySelect($event)"
  ></app-cloud-activity-feed>
</div>

<!-- ── Item settings drawer ──────────────────────────────────────── -->
<app-item-settings-drawer
  [service]="serviceCard"
  [itemId]="drawerItemId"
  [open]="drawerItemId !== null"
  (closed)="closeItemSettings()"
></app-item-settings-drawer>
